/**
 * Shipping label ZPL (4×6 at 203 dpi, ^PW812 / ^LL1218).
 * PDF labels use the same layout in `breaze-firebase-functions/.../shipping-label-v4.js`.
 */
import QRCode from "qrcode";
import { BREAZE_SHIPPING_LOGO_ZPL } from "./breazeShippingLogoZpl";

/** Zebra Font 0 / ^CI0 — ASCII placeholder for empty label fields. */
const ZPL_EMPTY = "-";

export interface ZebraShippingLabelData {
  orderId: string;
  jobId: string;
  shipmentDate: string;
  store: string;
  storePhone: string;
  storeAddress: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  geofence: string;
  waybillOrOrderKey: string;
  deliveryNotes?: string;
  parcelIndex?: number;
  parcelTotal?: number;
  weightLabel?: string;
  dimsLabel?: string;
  /** v4-native, multi-parcel-capable orders only — mirrors the backend's PDF renderer. */
  useParcelSuffix?: boolean;
}

function formatPrintQueueShipmentDate(createdAt: unknown): string {
  if (createdAt instanceof Date && !Number.isNaN(createdAt.getTime())) {
    return createdAt.toDateString();
  }
  if (typeof createdAt === "string" || typeof createdAt === "number") {
    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toDateString();
  }
  return "";
}

/** Maps printer-queue JSON (snake_case) to label fields. */
export function mapPrintQueueOrderToZebraLabel(order: unknown): ZebraShippingLabelData | null {
  if (!order || typeof order !== "object") {
    return null;
  }
  const o = order as Record<string, unknown>;
  const waybill = String(o.waybill ?? "").trim();
  const orderKey = String(o.order_key ?? "").trim();
  const parcelIndex =
    typeof o.parcel_index === "number" ? o.parcel_index : Number(o.parcel_index) || 1;
  const parcelTotal =
    typeof o.parcel_total === "number" ? o.parcel_total : Number(o.parcel_total) || 1;
  return {
    orderId: String(o.order_id ?? ""),
    jobId: String(o.job_id ?? ""),
    shipmentDate: formatPrintQueueShipmentDate(o.createdAt),
    store: String(o.store ?? ""),
    storePhone: String(o.store_phone_number ?? ""),
    storeAddress: String(o.store_address ?? ""),
    customerName: String(o.name ?? ""),
    customerPhone: String(o.phone_number ?? ""),
    customerAddress: String(o.address ?? ""),
    geofence: String(o.delivery_geofence ?? ""),
    waybillOrOrderKey: waybill || orderKey,
    deliveryNotes: String(o.note ?? o.delivery_notes ?? "").trim() || undefined,
    parcelIndex,
    parcelTotal,
    weightLabel: String(o.weight_label ?? "").trim() || undefined,
    dimsLabel: String(o.dims_label ?? "").trim() || undefined,
    useParcelSuffix: o.use_parcel_suffix === true,
  };
}

export function sanitizeZplValue(value: string): string {
  return value
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u00D7/g, "x")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\^/g, " ")
    .replace(/~/g, " ")
    .replace(/\r/g, " ")
    .replace(/\n/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/  +/g, " ")
    .trim();
}

export function clampZplValue(value: string, maxLength: number): string {
  return sanitizeZplValue(value).slice(0, maxLength);
}

function zplNameAddressBlock(
  name: string,
  address: string,
  nameMax: number,
  addrMax: number
): string {
  return `${clampZplValue(name, nameMax)}\\&${clampZplValue(address, addrMax)}`;
}

/**
 * ZPL `^FB` overprints any overflow onto its last line (garbled double-struck
 * text), so like the PDF renderer's `zplTextFit` we shrink the font until the
 * estimated wrapped text fits the block's vertical space.
 * Font 0 (bold condensed) average char width ≈ 0.55 × height — deliberately
 * wide so the estimate overshoots and the printer never overprints.
 */
const ZPL_CHAR_WIDTH_RATIO = 0.55;

function wrappedLineCount(text: string, charsPerLine: number): number {
  // `\&` is the ZPL manual line break
  return text.split("\\&").reduce((total, segment) => {
    const words = segment.split(" ");
    let lines = 1;
    let lineLen = 0;
    for (const word of words) {
      const needed = lineLen === 0 ? word.length : lineLen + 1 + word.length;
      if (needed <= charsPerLine) {
        lineLen = needed;
      } else if (word.length <= charsPerLine) {
        lines += 1;
        lineLen = word.length;
      } else {
        lines += Math.ceil(word.length / charsPerLine);
        lineLen = word.length % charsPerLine || charsPerLine;
      }
    }
    return total + lines;
  }, 0);
}

function fitZplBlock(
  text: string,
  widthDots: number,
  heightDots: number,
  maxFont: number,
  minFont: number
): { font: number; maxLines: number } {
  let font = maxFont;
  while (font > minFont) {
    const charsPerLine = Math.max(1, Math.floor(widthDots / (font * ZPL_CHAR_WIDTH_RATIO)));
    if (wrappedLineCount(text, charsPerLine) * font <= heightDots) {
      break;
    }
    font -= 2;
  }
  return { font, maxLines: Math.max(1, Math.floor(heightDots / font)) };
}

/**
 * QR rendered as a fixed-size `^GFA` bitmap instead of `^BQ` — printer
 * firmware draws `^BQ` at unpredictable sizes (one tested unit ~2x spec,
 * ignoring the magnification parameter), while a bitmap prints pixel-exact
 * on any 203dpi printer. Same encoder + ECC M as the PDF label's QR.
 */
export function qrGfaZpl(data: string, targetDots: number): string {
  const modules = QRCode.create(data, { errorCorrectionLevel: "M" }).modules;
  const size = modules.size;
  const scale = Math.max(2, Math.floor(targetDots / size));
  const bytesPerRow = Math.ceil((size * scale) / 8);
  const rows: string[] = [];
  for (let r = 0; r < size; r++) {
    const row = new Uint8Array(bytesPerRow);
    for (let c = 0; c < size; c++) {
      if (modules.get(r, c)) {
        for (let i = 0; i < scale; i++) {
          const x = c * scale + i;
          row[x >> 3] |= 0x80 >> (x & 7);
        }
      }
    }
    const hex = Array.from(row, (b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
    for (let i = 0; i < scale; i++) {
      rows.push(hex);
    }
  }
  const total = bytesPerRow * rows.length;
  return `^GFA,${total},${total},${bytesPerRow},${rows.join("")}`;
}

function routeCodeFromGeofence(geofence: string): string {
  const g = geofence.trim();
  if (!g) {
    return ZPL_EMPTY;
  }
  return clampZplValue(g.replace(/\s*[\u2013\u2014-]\s*/g, "-").replace(/\s+/g, "-"), 32);
}

/** Sanitized, whitespace-stripped waybill/order-key, or "UNKNOWN" if empty. */
function baseParcelCode(waybill: string): string {
  return sanitizeZplValue(waybill).replace(/\s+/g, "") || "UNKNOWN";
}

/**
 * Per-parcel code `<waybill>-<index>/<total>` — identical to the backend
 * `formatParcelLabel` and reversible by `parseScan`. Used for both the QR +
 * vertical-barcode payloads (so each parcel scans to itself) and the printed
 * human line (so the text and barcodes agree). v3 orders never get this
 * suffix (see `useParcelSuffix` on `ZebraShippingLabelData`) — their scanning
 * workflow expects the bare waybill, matching the backend PDF renderer.
 */
function parcelHumanLine(waybill: string, index: number, total: number): string {
  return `${baseParcelCode(waybill)}-${index}/${total}`;
}

function buildSingleLabelZpl(label: ZebraShippingLabelData): string {
  const pi = label.parcelIndex && label.parcelIndex > 0 ? label.parcelIndex : 1;
  const pt = label.parcelTotal && label.parcelTotal > 0 ? label.parcelTotal : 1;
  // Per-parcel scan code carried by BOTH the main horizontal and the vertical
  // Code-128 barcodes, which round-trips the full `<waybill>-<index>/<total>`
  // through parseScan so each printed box scans to its own parcel — v4-native
  // orders only. v3 orders (useParcelSuffix false/undefined) keep the bare
  // waybill on both barcodes, matching the backend PDF renderer and v3's
  // scanning workflow, which expects no suffix. The caption text next to each
  // barcode always mirrors this same value, suffixed or not — never left
  // blank. The QR carries the bare waybill either way.
  const parcelCode = label.useParcelSuffix
    ? parcelHumanLine(label.waybillOrOrderKey, pi, pt)
    : baseParcelCode(label.waybillOrOrderKey);
  const qrData = clampZplValue(
    sanitizeZplValue(label.waybillOrOrderKey).replace(/\s+/g, "") || "N/A",
    48
  );
  const barcodeData = clampZplValue(parcelCode || "N/A", 48);
  // Matches the PDF label: QR image at (640,25), ~120 dots square, caption
  // at y=150. Bitmap size is capped at targetDots, so it always clears the
  // caption and the divider at y=225.
  const qrBitmap = qrGfaZpl(qrData, 120);
  const fromBlock = zplNameAddressBlock(
    label.store.trim() || "Sender",
    [label.storeAddress, label.storePhone].filter(Boolean).join(", "),
    72,
    220
  );
  const toBlock = zplNameAddressBlock(
    label.customerName.trim() || "Recipient",
    [label.customerAddress, label.customerPhone].filter(Boolean).join(", "),
    72,
    260
  );
  const route = routeCodeFromGeofence(label.geofence);
  const notesRaw = (label.deliveryNotes ?? "").trim();
  const notesDisplay = notesRaw ? `Notes: ${clampZplValue(notesRaw, 420)}` : `Notes: ${ZPL_EMPTY}`;
  const orderIdLine = `Order ID: ${clampZplValue(label.orderId || ZPL_EMPTY, 48)}`;
  const verticalHuman = clampZplValue(parcelCode, 44);
  const humanUnderMain = clampZplValue(parcelCode, 48);
  // Vertical space per block: FROM y70→divider y225; TO y245→notes y530;
  // notes y530→order ID y630.
  const fromFit = fitZplBlock(fromBlock, 240, 150, 30, 18);
  const toFit = fitZplBlock(toBlock, 420, 280, 40, 22);
  const notesFit = fitZplBlock(notesDisplay, 600, 95, 24, 16);
  const weightLine = `WEIGHT: ${clampZplValue(label.weightLabel?.trim() || ZPL_EMPTY, 24)}`;
  const dimsLine = `DIMS: ${clampZplValue(label.dimsLabel?.trim() || ZPL_EMPTY, 28)}`;
  // v3 orders are never multi-parcel-aware — leave the pod blank instead of "1 OF 1".
  const parcelPod = label.useParcelSuffix ? `Parcel: ${pi} OF ${pt}` : "";

  const logoZpl =
    BREAZE_SHIPPING_LOGO_ZPL.trim() ||
    `^FO25,28^A0N,30,30^FDBreaze^FS
^FO25,54^A0N,22,22^FDdelivery^FS`;

  return `^XA

^FX --- SETUP ---
^PW812
^LL1218
^PON

^FX --- SECTION 1: LOGO & SENDER ---
^FX --- Breaze Delivery Logo ---
${logoZpl}

^FX Pickup Details
^FO400,30^A0N,35,35^FDFROM:^FS
^FX Manual break (\\&) ONLY after the name. Address wraps naturally.
^FO400,70^A0N,${fromFit.font},${fromFit.font}^FB240,${fromFit.maxLines},0,L,0^FD${fromBlock}^FS

^FX Backup QR Code (Far Right Axis) as a fixed-size bitmap:
^FX printer firmware draws native QR fields at unpredictable sizes.
^FO640,25${qrBitmap}^FS
^FX Waybill caption below the QR code (matches PDF layout)
^FO640,150^A0N,18,18^FD${qrData}^FS

^FX Divider Line 1
^FO10,225^GB790,3,3^FS

^FX --- SECTION 2: RECIPIENT & ROUTE ---
^FX "TO" Inverted Block
^FO25,245^GB100,60,60^FS
^FO35,255^A0N,40,40^FR^FDTO:^FS

^FX Backup Vertical Barcode
^FX Graphic anchored at X=45, Y=315. Grows downwards.
^FO45,315^BY2,2,45^BCR,45,N,N,N,A^FD${barcodeData}^FS
^FX Text anchored next to it at Y=315 (flush with vertical start)
^FO100,315^A0R,14,14^FD${verticalHuman}^FS

^FX Delivery Details
^FX Manual break (\\&) ONLY after the name. Address wraps naturally.
^FO150,245^A0N,${toFit.font},${toFit.font}^FB420,${toFit.maxLines},0,L,0^FD${toBlock}^FS

^FX Route Box
^FO580,390^GB210,80,3^FS
^FO590,400^A0N,25,25^FDROUTE:^FS
^FO590,430^A0N,35,35^FD${route}^FS

^FX *** NEW: Delivery Notes ***
^FO150,530^A0N,${notesFit.font},${notesFit.font}^FB600,${notesFit.maxLines},0,L,0^FD${notesDisplay}^FS

^FX *** NEW: Order ID ***
^FO150,630^A0N,35,35^FD${orderIdLine}^FS

^FX --- SECTION 3: MAIN BARCODE ---
^FX Barcode pushed down to Y=690 to clear vertical barcode
^FO150,690^BY3,3,140^BCN,140,N,N,N^FD${barcodeData}^FS
^FO150,840^A0N,35,35^FD${humanUnderMain}^FS

^FX Divider Line 3
^FO10,890^GB790,3,3^FS

^FX --- SECTION 4: WEIGHT & DIMS ---
^FO30,910^A0N,35,35^FD${weightLine}^FS
^FX Vertical Divider
^FO400,890^GB3,60,3^FS
^FO420,910^A0N,35,35^FD${dimsLine}^FS

^FX --- SECTION 5: PROOF OF DELIVERY ---
^FX Black Header Bar shifted down to compress the signature area
^FO10,950^GB790,50,50^FS
^FO30,960^A0N,30,30^FR^FDPROOF OF DELIVERY^FS

^FX *** NEW: Dynamic Parcel Count Reversed in Black Bar ***
^FO600,960^A0N,30,30^FR^FB190,1,0,R^FD${parcelPod}^FS

^FX Signature Lines (Slightly lowered to balance the narrowed box)
^FO40,1100^GB400,3,3^FS
^FO40,1110^A0N,30,30^FDCUSTOMER SIGNATURE:^FS

^FO500,1100^GB280,3,3^FS
^FO500,1110^A0N,30,30^FDDATE / TIME:^FS

^FX Disclaimer
^FO40,1160^A0N,20,20^FD*Signature required to confirm receipt.*^FS

^XZ`;
}

export function buildZebraShippingLabelZpl(labels: ZebraShippingLabelData[]): string {
  return labels.map((label) => buildSingleLabelZpl(label)).join("\n");
}
